import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n, useT } from '../lib/i18n'
import { INTENTS, completeWelcome, type Intent } from '../lib/guest'
import { IS_MARKETPLACE } from '../lib/market'
import { Icon, type IconName } from '../components/ui'
import { Brand } from '../components/Brand'

interface Language { code: string; name: string; native_name: string }

/**
 * The first screen anyone sees. Two questions, then out of the way.
 *
 * It replaces the sign-in form that used to stand here. Nothing on it is
 * asked twice and nothing on it is permanent -- Account changes both -- which
 * is why it can afford to be the very first thing and still not feel like a
 * form.
 *
 * The language applies the INSTANT it is tapped, not on Start, so the rest of
 * this screen is already in the person's own language before they answer the
 * second question. That is the whole reason the language question is first.
 *
 * And the whole screen is translated, options included (0041). A welcome that
 * asks "what brings you here?" in Hausa and then lists the three answers in
 * English has translated the furniture and not the question.
 */

// The van glyph is historically called `send`, from when sending goods was
// the only product. It is the right picture for "a whole vehicle", so it is
// reused here rather than renamed across the app for one screen.
const ICON: Record<Intent, IconName> = {
  charter: 'send',
  seat: 'seat',
  send: 'box',
}

export default function Welcome({ onDone }: { onDone: (intent: Intent) => void }) {
  const msg = useT()
  const { locale, setLocale, dir } = useI18n()
  const [intent, setIntent] = useState<Intent>('charter')

  // Public reference data -- this screen runs before anyone is signed in, so
  // it can only call endpoints that tolerate no caller.
  const languages = useQuery<Language[]>({
    queryKey: ['languages'], queryFn: () => api('/languages'),
  })
  const langs = languages.data ?? []

  return (
    <div className="flex h-full flex-col overflow-y-auto px-6" dir={dir}>
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col"
           style={{ paddingTop: 'calc(34px + env(safe-area-inset-top,0px))' }}>
        <Brand />

        <h1 className="mt-6 font-display text-[30px] font-extrabold leading-[1.1] tracking-tight">
          {msg('welcome.title')}
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-3">{msg('welcome.sub')}</p>

        {/* ------------------------------------------------ 1. the language */}
        <p className="label mt-8 mb-2 block">{msg('welcome.yourLanguage')}</p>
        <div className="grid grid-cols-2 gap-2">
          {langs.map((l) => (
            <button
              key={l.code}
              type="button"
              aria-pressed={l.code === locale}
              onClick={() => setLocale(l.code)}
              className={`min-h-[44px] rounded-xl border px-3 text-[13px] font-semibold transition ${
                l.code === locale
                  ? 'border-accent bg-accent text-white'
                  : 'border-line bg-surface-2 text-ink-2'
              }`}
            >
              {l.native_name || l.name}
            </button>
          ))}
        </div>

        {/*
          -------------------------------------------------- 2. the intent

          DIRECTORY BUILD: there is no intent question.

          All three answers now lead to the same screen -- the carrier list
          -- because what is chartered, sent or seated is agreed with the
          company on his own site. A question whose three answers do the
          same thing teaches someone that the app is not listening.
        */}
        {IS_MARKETPLACE && (<>
        <p className="label mt-8 mb-2 block">{msg('welcome.whatBringsYou')}</p>
        <div className="flex flex-col gap-2.5">
          {INTENTS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={k === intent}
              onClick={() => setIntent(k)}
              className={`flex w-full items-center gap-3 rounded-2xl border p-3.5 text-start transition ${
                k === intent ? 'border-accent bg-accent-wash' : 'border-line bg-surface-2'
              }`}
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                k === intent ? 'bg-accent/20' : 'bg-surface'
              }`}>
                <Icon name={ICON[k]} className="h-[17px] w-[17px] text-accent" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold">{msg(`welcome.${k}.title`)}</span>
                <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-3">
                  {msg(`welcome.${k}.body`)}
                </span>
              </span>
              <span className={`h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] ${
                k === intent ? 'border-accent bg-accent' : 'border-line'
              }`} />
            </button>
          ))}
        </div>
        </>)}

        <div className="mt-8 pb-10">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => { completeWelcome(intent); onDone(intent) }}
          >
            {msg('welcome.start')}
          </button>
          <p className="mt-3 text-center text-[11.5px] text-ink-3">{msg('welcome.later')}</p>
        </div>
      </div>
    </div>
  )
}
